import React, {
    ComponentProps,
    FC,
    JSXElementConstructor,
    PropsWithChildren,
    ReactElement,
} from "react";

import { css, Global } from "@emotion/react";
import styled from "@emotion/styled";
import classNames from "classnames";

export type Breakpoint =
    | "mobile"
    | "tablet"
    | "desktop"
    | "widescreen"
    | "fullhd";

export type ColumnSize =
    | "full"
    | "half"
    | `one-${"third" | "quarter" | "fifth"}`
    | "two-thirds"
    | "three-quarters"
    | `${"two" | "three" | "four"}-fifths`
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6
    | 7
    | 8
    | 9
    | 10
    | 11
    | 12;

// Bulma's grid CSS previously supplied all of the below (base .column/.columns
// rules, plus a width rule for every is-<size>-<breakpoint> combination).
// Since Bulma is being removed, this Global replaces it once, app-wide -
// Column/Columns keep generating the exact same classNames as before.
const namedSizeWidths: Record<string, string> = {
    full: "100%",
    half: "50%",
    "one-third": "33.3333%",
    "one-quarter": "25%",
    "one-fifth": "20%",
    "two-thirds": "66.6666%",
    "three-quarters": "75%",
    "two-fifths": "40%",
    "three-fifths": "60%",
    "four-fifths": "80%",
};

const allSizes: (string | number)[] = [
    ...Object.keys(namedSizeWidths),
    ...Array.from({ length: 12 }, (_, i) => i + 1),
];

const widthFor = (size: string | number): string =>
    typeof size === "number" ? `${(size / 12) * 100}%` : namedSizeWidths[size];

const sizeRulesFor = (suffix: string) =>
    allSizes
        .map(
            (size) => `
        &.is-${size}-${suffix} {
            flex: none;
            width: ${widthFor(size)};
        }
    `
        )
        .join("\n");

const offsetRulesFor = (suffix: string) =>
    allSizes
        .map(
            (size) => `
        &.is-offset-${size}-${suffix} {
            margin-left: ${widthFor(size)};
        }
    `
        )
        .join("\n");

export const gridStyles = css`
    .column {
        display: block;
        flex-basis: 0;
        flex-grow: 1;
        flex-shrink: 1;
        padding: 0.75rem;

        &.is-narrow {
            flex: none;
            width: unset;
        }

        @media screen and (max-width: 767px) {
            ${sizeRulesFor("mobile")}
            ${offsetRulesFor("mobile")}
        }

        @media screen and (min-width: 768px) {
            ${sizeRulesFor("tablet")}
            ${offsetRulesFor("tablet")}
        }

        @media screen and (min-width: 1024px) {
            ${sizeRulesFor("desktop")}
            ${offsetRulesFor("desktop")}
        }

        @media screen and (min-width: 1216px) {
            ${sizeRulesFor("widescreen")}
            ${offsetRulesFor("widescreen")}
        }

        @media screen and (min-width: 1408px) {
            ${sizeRulesFor("fullhd")}
            ${offsetRulesFor("fullhd")}
        }
    }

    .columns {
        margin-left: -0.75rem;
        margin-right: -0.75rem;
        margin-top: -0.75rem;

        &:last-child {
            margin-bottom: -0.75rem;
        }

        &:not(:last-child) {
            margin-bottom: 0.75rem;
        }

        &.is-centered {
            justify-content: center;
        }

        &.is-multiline {
            flex-wrap: wrap;
        }

        &.is-mobile {
            display: flex;
        }

        @media screen and (min-width: 768px) {
            &:not(.is-desktop) {
                display: flex;
            }
        }

        @media screen and (min-width: 1024px) {
            &.is-desktop {
                display: flex;
            }
        }
    }
`;

// Mounted by both Column and Columns so the rules above are present
// regardless of which one gets rendered first; Emotion dedupes identical
// injected styles, so this is cheap even though it's mounted many times.
export const GridGlobalStyles = () => <Global styles={gridStyles} />;

export type ColumnProps = Partial<Record<Breakpoint, ColumnSize>> & {
    className?: string;
};

export const Column: FC<ColumnProps> = ({
    mobile,
    tablet = mobile,
    desktop = tablet,
    widescreen = desktop,
    fullhd = widescreen,
    className,
    ...props
}) => (
    <>
        <GridGlobalStyles />
        <div
            className={classNames(
                "column",
                mobile && `is-${mobile}-mobile`,
                tablet && `is-${tablet}-tablet`,
                desktop && `is-${desktop}-desktop`,
                widescreen && `is-${widescreen}-widescreen`,
                fullhd && `is-${fullhd}-fullhd`,
                className
            )}
            {...props}
        />
    </>
);

const preparedColumn = <ks extends Breakpoint>(
    props2: Record<ks, ColumnSize>,
    displayName: string
) => {
    const Column_ = (props: Omit<ColumnProps, ks>) => (
        <Column {...props} {...props2} />
    );
    Object.assign(Column_, { displayName });

    return Column_;
};

export const ColumnFull = preparedColumn({ mobile: "full" }, "ColumnFull");

export const ColumnTwoThirds = preparedColumn(
    {
        mobile: "full",
        tablet: "full",
        desktop: "two-thirds",
    },
    "ColumnTwoThirds"
);

export const ColumnHalf = preparedColumn(
    { mobile: "full", tablet: "half" },
    "ColumnHalf"
);

export const ColumnOneThird = preparedColumn(
    {
        mobile: "full",
        tablet: "full",
        desktop: "one-third",
    },
    "ColumnOneThird"
);

export const ColumnOneQuarter = preparedColumn(
    {
        mobile: "full",
        tablet: "full",
        desktop: "one-quarter",
    },
    "ColumnOneQuarter"
);

export const ColumnLarge = preparedColumn(
    { mobile: "full", tablet: "half", desktop: "two-thirds" },
    "ColumnLarge"
);
/** @deprecated */
export const LargeColumn = ColumnLarge;

export const ColumnSmall = preparedColumn(
    {
        mobile: "full",
        tablet: "half",
        desktop: "one-third",
    },
    "ColumnSmall"
);
/** @deprecated */
export const SmallColumn = ColumnSmall;

export type ColumnsProps<
    renderAs extends
        | keyof JSX.IntrinsicElements
        | JSXElementConstructor<any> = "div"
> = {
    renderAs?: renderAs;
    sameHeight?: boolean;
    centered?: boolean;
    className?: string;
} & ComponentProps<renderAs>;

const RawColumns = <
    renderAs extends
        | keyof JSX.IntrinsicElements
        | JSXElementConstructor<any> = "div"
>({
    renderAs: Component = "div" as any,
    sameHeight,
    centered,
    className,
    ...props
}: PropsWithChildren<ColumnsProps<renderAs>>): ReactElement<
    any,
    any
> | null => (
    <>
        <GridGlobalStyles />
        <Component
            className={classNames(
                "columns",
                "is-multiline",
                {
                    "is-centered": centered,
                },
                className
            )}
            {...props}
        />
    </>
);

const Columns = Object.assign(
    styled(RawColumns)`
        ${({ sameHeight }) =>
            sameHeight &&
            css`
                .column > div {
                    height: 100%;
                }
            `}
    `,
    {
        /** @deprecated */
        Column: Column,
        /** @deprecated */
        Full: ColumnFull,
        /** @deprecated */
        TwoThirds: ColumnTwoThirds,
        /** @deprecated */
        Half: ColumnHalf,
        /** @deprecated */
        OneThird: ColumnOneThird,
        /** @deprecated */
        OneQuarter: ColumnOneQuarter,
    }
);
Columns.defaultProps = {
    sameHeight: false,
    centered: false,
};
export default Columns;
